import React, { useState, useEffect } from "react";
import AddSubModal from "./AddSubModal";
import {
  recruitJobsApis,
  rolesSettingsApis,
  departmentHRApi,
  employeeHRApis,
  BusinessSettingsApi,
  recruitJobApplicationApis,
} from "../../../Data/axios";
import {
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Modal,
  Box,
  Typography,
  Slide,
  Grid,
  Checkbox,
  FormControlLabel,
  Chip,
  Autocomplete,
  Card,
  CardContent,
  MenuItem,
} from "@mui/material";
import {
  HelpOutline,
  FormatListBulleted,
  FormatListNumbered,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  InsertLink,
  Image,
  Code,
  FormatQuote,
  FormatClear,
  CloudUpload,
  Delete,
} from "@mui/icons-material";

export default function AddJobModal({
  open,
  handleClose,
  handleSave,
  tableApis,
}) {
  // Generate applicant_id using today's date
  const generateApplicantId = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const hours = String(today.getHours()).padStart(2, "0");
    const minutes = String(today.getMinutes()).padStart(2, "0");
    const seconds = String(today.getSeconds()).padStart(2, "0");
    return `CAD${year}${month}${day}${hours}${minutes}${seconds}`;
  };

  // Add form state for all fields - updated to match backend
  const [formData, setFormData] = useState({
    applicant_id: generateApplicantId(),
    job_id: "",
    applicant_name: "",
    applicant_email: "",
    applicant_phone: "",
    applicant_current_location: "", // This will now be a string input
    applicant_exp: "",
    applicant_notice_period: "",
    status: "0",
    applicationSource: "",
    cover_letter: null,
    resume: null,
  });

  // Add file name states for display
  const [fileNames, setFileNames] = useState({
    cover_letter: "",
    resume: "",
  });

  // Add field errors state
  const [fieldErrors, setFieldErrors] = useState({});

  // States for API data - removed locations state
  const [jobs, setJobs] = useState([]);
  const [workExperience, setWorkExperience] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Hardcoded options (keeping notice period and application source as hardcoded)
  const noticePeriodOptions = [
    { value: "immediate", label: "Immediate" },
    { value: "15-days", label: "15 Days" },
    { value: "1-month", label: "1 Month" },
    { value: "2-months", label: "2 Months" },
    { value: "3-months", label: "3 Months" },
  ];

  const applicationSourceOptions = [
    { value: "website", label: "Website" },
    { value: "linkedin", label: "LinkedIn" },
    { value: "referral", label: "Referral" },
    { value: "job-portal", label: "Job Portal" },
    { value: "direct", label: "Direct Application" },
  ];

  // Fetch data on component mount
  useEffect(() => {
    if (open) {
      const fetchAllData = async () => {
        setLoading(true);
        try {
          // Fetch jobs from API
          const jobsResponse = await recruitJobsApis.getAllJobTitle();
          const jobsData = jobsResponse.data.rjobs.data;
          setJobs(jobsData);

          // Fetch work experience from API
          const workExperienceResponse =
            await recruitJobsApis.getWorkExperience();
          const workExperienceData =
            workExperienceResponse.data.work_experiences.data;
          console.log("Work Experience Data:", workExperienceData);
          setWorkExperience(workExperienceData);

          // Removed locations fetch
        } catch (err) {
          console.error("Error fetching data:", err);
          setError("Failed to fetch data. Please try again later.");
        } finally {
          setLoading(false);
        }
      };

      fetchAllData();
    }
  }, [open, tableApis]);

  // Handle form field changes - clear error when user starts typing
  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Handle checkbox changes
  const handleCheckboxChange = (field, checked) => {
    setFormData((prev) => ({
      ...prev,
      [field]: checked,
    }));
  };

  // Handle file upload
  const handleFileUpload = (field, file) => {
    if (file) {
      setFormData((prev) => ({
        ...prev,
        [field]: file,
      }));
      setFileNames((prev) => ({
        ...prev,
        [field]: file.name,
      }));
    }
  };

  // Handle file removal
  const handleFileRemove = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: null,
    }));
    setFileNames((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  // Validate required fields - updated to set field-specific errors
  const validateForm = () => {
    const requiredFields = {
      job_id: "Job is required",
      applicant_name: "Name is required",
      applicant_phone: "Phone is required",
    };

    const errors = {};
    let hasErrors = false;

    Object.entries(requiredFields).forEach(([field, message]) => {
      if (!formData[field] || formData[field] === "--") {
        errors[field] = message;
        hasErrors = true;
      }
    });

    setFieldErrors(errors);
    return !hasErrors;
  };

  // Handle form submission
  const handleSaveApplication = async () => {
    console.log("Add Job Application clicked:", formData);

    if (!validateForm()) {
      return;
    }

    // Create FormData for file upload
    const submitData = new FormData();

    // Append all form fields
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== null && formData[key] !== "") {
        submitData.append(key, formData[key]);
      }
    });

    recruitJobApplicationApis.add(submitData);

    // Call the parent's handleSave function to refresh data
    if (handleSave) {
      await handleSave();
    }

    // Close modal after successful save
    handleCloseModal();
  };

  // Reset form when modal closes - updated field names
  const handleCloseModal = () => {
    handleClose();
    setFormData({
      applicant_id: generateApplicantId(),
      job_id: "",
      applicant_name: "",
      applicant_email: "",
      applicant_phone: "",
      applicant_current_location: "", // This will now be a string input
      applicant_exp: "",
      applicant_notice_period: "",
      status: "0",
      applicationSource: "",
      cover_letter: null,
      resume: null,
    });
    setFileNames({
      cover_letter: "",
      resume: "",
    });
    setFieldErrors({});
  };

  // Define reusable input styling
  const inputProps = {
    sx: {
      bgcolor: "#f9f9f9",
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: "#e0e0e0",
        },
        "&:hover fieldset": {
          borderColor: "#bdbdbd",
        },
      },
      "& .MuiSelect-select": {
        color: "#424242",
      },
      "& .MuiInputBase-input": {
        color: "#424242",
      },
    },
  };

  const checkboxStyle = {
    color: "#9e9e9e",
    "&.Mui-checked": {
      color: "#4285F4",
    },
  };

  return (
    <>
      <Modal open={open} onClose={handleCloseModal} closeAfterTransition>
        <Slide direction="left" in={open}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "85%",
              height: "100%",
              bgcolor: "#f5f5f5",
              boxShadow: 24,
              p: 4,
              overflow: "auto",
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <Typography variant="h3" component="h2" sx={{ color: "#212121" }}>
                Add Job Application
              </Typography>
              <Button
                onClick={handleCloseModal}
                variant="outlined"
                sx={{
                  borderColor: "#bdbdbd",
                  color: "#424242",
                  "&:hover": {
                    borderColor: "#9e9e9e",
                    bgcolor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                Close
              </Button>
            </Box>

            <Card
              sx={{
                p: 2,
                bgcolor: "#ffffff",
                boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ color: "#212121", mb: 3 }}>
                  Create New
                </Typography>

                <Box sx={{ mb: 4 }}>
                  <Grid container spacing={3}>
                    {/* Applicant ID - Auto-generated and read-only */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Applicant ID
                      </Typography>
                      <TextField
                        fullWidth
                        value={formData.applicant_id}
                        size="small"
                        InputProps={{
                          ...inputProps,
                          readOnly: true,
                        }}
                        sx={{
                          ...inputProps.sx,
                          "& .MuiInputBase-input": {
                            color: "#757575",
                            backgroundColor: "#f0f0f0",
                          },
                        }}
                      />
                    </Grid>

                    {/* Jobs - updated field name */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Jobs <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        value={formData.job_id}
                        onChange={(e) =>
                          handleFormChange("job_id", e.target.value)
                        }
                        size="small"
                        error={!!fieldErrors.job_id}
                        InputProps={inputProps}
                      >
                        <MenuItem value="">--</MenuItem>
                        {jobs.map((job) => (
                          <MenuItem key={job.id} value={job.id}>
                            {job.title}
                          </MenuItem>
                        ))}
                      </TextField>
                      {fieldErrors.job_id && (
                        <Typography
                          variant="caption"
                          sx={{ color: "red", mt: 0.5, display: "block" }}
                        >
                          {fieldErrors.job_id}
                        </Typography>
                      )}
                    </Grid>

                    {/* Name - updated field name */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Name <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="e.g. John Doe"
                        size="small"
                        value={formData.applicant_name}
                        onChange={(e) =>
                          handleFormChange("applicant_name", e.target.value)
                        }
                        error={!!fieldErrors.applicant_name}
                        InputProps={inputProps}
                      />
                      {fieldErrors.applicant_name && (
                        <Typography
                          variant="caption"
                          sx={{ color: "red", mt: 0.5, display: "block" }}
                        >
                          {fieldErrors.applicant_name}
                        </Typography>
                      )}
                    </Grid>

                    {/* Email - updated field name */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Email
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="e.g. johndoe@example.com"
                        size="small"
                        type="email"
                        value={formData.applicant_email}
                        onChange={(e) =>
                          handleFormChange("applicant_email", e.target.value)
                        }
                        InputProps={inputProps}
                      />
                    </Grid>

                    {/* Phone - updated field name */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Phone <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="e.g. 987654321"
                        size="small"
                        value={formData.applicant_phone}
                        onChange={(e) =>
                          handleFormChange("applicant_phone", e.target.value)
                        }
                        error={!!fieldErrors.applicant_phone}
                        InputProps={inputProps}
                      />
                      {fieldErrors.applicant_phone && (
                        <Typography
                          variant="caption"
                          sx={{ color: "red", mt: 0.5, display: "block" }}
                        >
                          {fieldErrors.applicant_phone}
                        </Typography>
                      )}
                    </Grid>

                    {/* Current Location - changed to text input */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Current Location
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="e.g. California"
                        size="small"
                        value={formData.applicant_current_location}
                        onChange={(e) =>
                          handleFormChange(
                            "applicant_current_location",
                            e.target.value
                          )
                        }
                        InputProps={inputProps}
                      />
                    </Grid>

                    {/* Total Experience - updated field name */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Total Experience
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        value={formData.applicant_exp}
                        onChange={(e) =>
                          handleFormChange("applicant_exp", e.target.value)
                        }
                        size="small"
                        InputProps={inputProps}
                      >
                        <MenuItem value="">--</MenuItem>
                        {workExperience.map((experience) => (
                          <MenuItem key={experience.id} value={experience.id}>
                            {experience.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {/* Removed the Location dropdown grid item */}

                    {/* Notice Period - updated field name */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Notice Period
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        value={formData.applicant_notice_period}
                        onChange={(e) =>
                          handleFormChange(
                            "applicant_notice_period",
                            e.target.value
                          )
                        }
                        size="small"
                        InputProps={inputProps}
                      >
                        <MenuItem value="">--</MenuItem>
                        {noticePeriodOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {/* Status - updated field name and values to match backend */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Status
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        value={formData.status}
                        onChange={(e) =>
                          handleFormChange("status", e.target.value)
                        }
                        size="small"
                        InputProps={inputProps}
                      >
                        <MenuItem value="0">
                          <Box display="flex" alignItems="center">
                            <Box
                              component="span"
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "#000000",
                                display: "inline-block",
                                mr: 1,
                              }}
                            />
                            Applied
                          </Box>
                        </MenuItem>
                        <MenuItem value="1">
                          <Box display="flex" alignItems="center">
                            <Box
                              component="span"
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "#ff9800",
                                display: "inline-block",
                                mr: 1,
                              }}
                            />
                            Interview
                          </Box>
                        </MenuItem>
                        <MenuItem value="2">
                          <Box display="flex" alignItems="center">
                            <Box
                              component="span"
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "#4caf50",
                                display: "inline-block",
                                mr: 1,
                              }}
                            />
                            Hired
                          </Box>
                        </MenuItem>
                        <MenuItem value="3">
                          <Box display="flex" alignItems="center">
                            <Box
                              component="span"
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "#f44336",
                                display: "inline-block",
                                mr: 1,
                              }}
                            />
                            Rejected
                          </Box>
                        </MenuItem>
                      </TextField>
                    </Grid>

                    {/* Application Source */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Application Source
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        value={formData.applicationSource}
                        onChange={(e) =>
                          handleFormChange("applicationSource", e.target.value)
                        }
                        size="small"
                        InputProps={inputProps}
                      >
                        <MenuItem value="">--</MenuItem>
                        {applicationSourceOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {/* Empty space to the right of Application Source */}
                    <Grid item xs={12} md={9}>
                      {/* Empty grid item */}
                    </Grid>

                    {/* Resume File Upload */}
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Resume
                      </Typography>
                      <Box
                        sx={{
                          border: "2px dashed #e0e0e0",
                          borderRadius: "4px",
                          p: 2,
                          textAlign: "center",
                          bgcolor: "#f9f9f9",
                          position: "relative",
                        }}
                      >
                        {fileNames.resume ? (
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: "#424242" }}
                            >
                              {fileNames.resume}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleFileRemove("resume")}
                              sx={{ color: "#f44336" }}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        ) : (
                          <>
                            <input
                              accept=".pdf,.doc,.docx"
                              style={{ display: "none" }}
                              id="resume-upload"
                              type="file"
                              onChange={(e) =>
                                handleFileUpload("resume", e.target.files[0])
                              }
                            />
                            <label htmlFor="resume-upload">
                              <Button
                                variant="outlined"
                                component="span"
                                startIcon={<CloudUpload />}
                                sx={{
                                  borderColor: "#bdbdbd",
                                  color: "#424242",
                                  "&:hover": {
                                    borderColor: "#9e9e9e",
                                    bgcolor: "rgba(0, 0, 0, 0.04)",
                                  },
                                }}
                              >
                                Upload Resume
                              </Button>
                            </label>
                            <Typography
                              variant="caption"
                              sx={{ display: "block", mt: 1, color: "#757575" }}
                            >
                              Supported formats: PDF, DOC, DOCX
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Grid>

                    {/* Cover Letter File Upload */}
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Cover Letter
                      </Typography>
                      <Box
                        sx={{
                          border: "2px dashed #e0e0e0",
                          borderRadius: "4px",
                          p: 2,
                          textAlign: "center",
                          bgcolor: "#f9f9f9",
                          position: "relative",
                        }}
                      >
                        {fileNames.cover_letter ? (
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: "#424242" }}
                            >
                              {fileNames.cover_letter}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleFileRemove("cover_letter")}
                              sx={{ color: "#f44336" }}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        ) : (
                          <>
                            <input
                              accept=".pdf,.doc,.docx,.txt"
                              style={{ display: "none" }}
                              id="cover-letter-upload"
                              type="file"
                              onChange={(e) =>
                                handleFileUpload(
                                  "cover_letter",
                                  e.target.files[0]
                                )
                              }
                            />
                            <label htmlFor="cover-letter-upload">
                              <Button
                                variant="outlined"
                                component="span"
                                startIcon={<CloudUpload />}
                                sx={{
                                  borderColor: "#bdbdbd",
                                  color: "#424242",
                                  "&:hover": {
                                    borderColor: "#9e9e9e",
                                    bgcolor: "rgba(0, 0, 0, 0.04)",
                                  },
                                }}
                              >
                                Upload Cover Letter
                              </Button>
                            </label>
                            <Typography
                              variant="caption"
                              sx={{ display: "block", mt: 1, color: "#757575" }}
                            >
                              Supported formats: PDF, DOC, DOCX, TXT
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Form buttons */}
                <Box display="flex" gap={2} mt={3}>
                  <Button
                    variant="contained"
                    onClick={handleSaveApplication}
                    sx={{
                      bgcolor: "#4285F4",
                      color: "white",
                      "&:hover": { bgcolor: "#3367D6" },
                      fontWeight: 500,
                      textTransform: "none",
                      padding: "8px 16px",
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleCloseModal}
                    sx={{
                      borderColor: "#bdbdbd",
                      color: "#424242",
                      "&:hover": {
                        borderColor: "#9e9e9e",
                        bgcolor: "rgba(0, 0, 0, 0.04)",
                      },
                      textTransform: "none",
                      padding: "8px 16px",
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Slide>
      </Modal>
    </>
  );
}
